import { useTranslation } from "react-i18next";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { type ISingleProject } from "@/types/(company)/projects.interface";
import {
  Building2,
  CalendarDaysIcon,
  FolderDot,
  Landmark,
  TextAlignStart,
  User2,
  type LucideIcon,
} from "lucide-react";
import { usePageTitle } from "@/hooks/usePageTitle";
import { useGetProjectDetailsByIdQuery } from "@/services/(company)/api.projects";
import { useParams } from "react-router-dom";
import { EmptyState } from "@/components/EmptyState";
import { Spinner } from "@/components/ui/v2/spinner";
import { type ReactNode } from "react";
import type { projectIdKey } from "@/routes/company/routes.projects.constants";
import { cn } from "@/utils/cn";
import { useLocale } from "@/hooks/useLocale";
import { withBranding } from "@/utils/helper";

const getTimelineProgress = (
  start: string | null,
  end: string | null,
): number | null => {
  if (!start || !end) return null;
  const startMs = new Date(start).getTime();
  const endMs = new Date(end).getTime();
  const now = Date.now();
  if (now <= startMs) return 0;
  if (now >= endMs) return 100;
  return Math.round(((now - startMs) / (endMs - startMs)) * 100);
};

const InfoCard = ({
  icon,
  iconColor,
  accent,
  title,
  children,
  className,
}: {
  icon: ReactNode;
  iconColor: string;
  accent: string;
  title: string;
  children: ReactNode;
  className?: string;
}) => (
  <div
    className={cn(
      "rounded-xs border border-gray-100 bg-white shadow-sm overflow-hidden",
      className,
    )}
  >
    <div
      className={cn(
        "flex items-center gap-2.5 px-4 py-2.5 border-b border-gray-100",
        accent,
      )}
    >
      <span className={iconColor}>{icon}</span>
      <span className="text-xs font-semibold text-gray-600 uppercase tracking-wide">
        {title}
      </span>
    </div>
    <div className="p-4">{children}</div>
  </div>
);

const InfoRow = ({
  icon: Icon,
  color,
  label,
  value,
}: {
  icon: LucideIcon;
  color: string;
  label: string;
  value: ReactNode;
}) => (
  <div className="flex items-start gap-3">
    <div className={`p-1.5 rounded-xs bg-${color}-100 shrink-0 mt-0.5`}>
      <Icon size={14} className={`text-${color}-500`} />
    </div>
    <div className="min-w-0">
      <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider mb-0.5">
        {label}
      </p>
      {typeof value === "string" ? (
        <p className="text-sm text-gray-800 font-medium">{value}</p>
      ) : (
        value
      )}
    </div>
  </div>
);

export const TabProjectOverview = () => {
  const { t } = useTranslation(["projects", "common"]);
  usePageTitle(withBranding(t("projects:project_overview")));
  const { projectId } = useParams<{ [projectIdKey]: string }>();

  const { data: { data: project } = {}, isLoading } =
    useGetProjectDetailsByIdQuery(
      { projectId: projectId! },
      { skip: !projectId, refetchOnMountOrArgChange: true },
    );

  if (isLoading) return <Spinner />;
  if (!projectId || !project)
    return (
      <EmptyState
        title={t("projects:project_not_found")}
        description={t(
          "projects:the_project_that_you_are_looking_for_cannot_be_found",
        )}
        icon={FolderDot}
      />
    );

  return (
    <div className="flex flex-col gap-4 pb-4">
      <InfoGrid project={project} />
    </div>
  );
};

const InfoGrid = ({ project }: { project: ISingleProject }) => {
  const { t } = useTranslation(["projects", "common"]);
  const { formatDate, formatNumber } = useLocale();
  const progress = getTimelineProgress(project.startDate, project.endDate);

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
      {/* Organization */}
      <InfoCard
        icon={<Building2 size={14} />}
        iconColor="text-blue-500"
        accent="bg-blue-50"
        title={t("projects:organization")}
      >
        <div className="flex flex-col gap-4">
          <InfoRow
            icon={Building2}
            color="blue"
            label={t("common:ui_kit.branch")}
            value={project.branch?.name ?? "—"}
          />
          <InfoRow
            icon={Landmark}
            color="indigo"
            label={t("common:labels.department")}
            value={project.department?.name ?? "—"}
          />
        </div>
      </InfoCard>

      {/* Timeline */}
      <InfoCard
        icon={<CalendarDaysIcon size={14} />}
        iconColor="text-emerald-600"
        accent="bg-emerald-50"
        title={t("projects:timeline")}
      >
        <div className="flex flex-col gap-4">
          <InfoRow
            icon={CalendarDaysIcon}
            color="green"
            label={t("projects:start_date")}
            value={
              project.startDate
                ? formatDate(new Date(project.startDate), "dd MMM, yyyy")
                : "—"
            }
          />
          <InfoRow
            icon={CalendarDaysIcon}
            color="pink"
            label={t("projects:end_date")}
            value={
              project.endDate
                ? formatDate(new Date(project.endDate), "dd MMM, yyyy")
                : t("projects:not_set")
            }
          />
          {progress !== null && (
            <div className="mt-1">
              <div className="flex justify-between text-[10px] text-gray-400 font-medium mb-1.5 uppercase tracking-wide">
                <span>{t("common:shared_ui.progress")}</span>
                <span>
                  {formatNumber(progress / 100, { style: "percent" })}
                </span>
              </div>
              <div className="h-1.5 rounded-xs bg-gray-100 overflow-hidden">
                <div
                  className={cn(
                    "h-full rounded-xs transition-all",
                    progress === 100
                      ? "bg-emerald-500"
                      : progress > 75
                        ? "bg-amber-400"
                        : "bg-primary",
                  )}
                  style={{ width: `${progress}%` }}
                />
              </div>
            </div>
          )}
        </div>
      </InfoCard>

      {/* Team */}
      <InfoCard
        icon={<User2 size={14} />}
        iconColor="text-purple-500"
        accent="bg-yellow-50"
        title={t("projects:team")}
      >
        <div className="flex flex-col gap-4">
          {/* Project Owner */}
          <div>
            <p className="text-[10px] font-semibold text-gray-600 uppercase tracking-wider mb-1.5">
              {t("projects:project_owner")}
            </p>
            {project.projectOwner ? (
              /*
                `projectOwner` is free text — it holds a team as often as a person
                ("Engineering Leadership"), so it is not a user and has no photo to
                show. A square tile with a building mark says "an owning party",
                where a round face circle would claim a person we do not have.
              */
              <div className="flex items-center gap-2">
                <span className="size-7 shrink-0 rounded-sm border border-gray-100 bg-orange-50 text-orange-600 shadow-sm flex items-center justify-center">
                  <Building2 size={13} />
                </span>
                <span className="text-sm text-gray-800 font-medium">
                  {project.projectOwner}
                </span>
              </div>
            ) : (
              <p className="text-sm text-gray-800 font-medium">—</p>
            )}
          </div>

          {/* Project Managers */}
          <div>
            <p className="text-[10px] font-semibold text-gray-600 uppercase tracking-wider mb-1.5">
              {t("projects:project_manager_s")}
            </p>
            {project.projectManagers?.length ? (
              <div className="flex flex-col gap-1.5">
                {project.projectManagers.map((m) => (
                  <div key={m.id} className="flex items-center gap-2">
                    <Avatar className="size-7 border border-gray-100 shadow-sm shrink-0">
                      <AvatarImage src={m.profileImage ?? undefined} />
                      <AvatarFallback className="text-[10px] bg-violet-50 text-violet-600 font-semibold">
                        {m.name?.slice(0, 2).toUpperCase() ?? "?"}
                      </AvatarFallback>
                    </Avatar>
                    <span className="text-sm text-gray-800 font-medium truncate">
                      {m.name}
                    </span>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-gray-800 font-medium">—</p>
            )}
          </div>
        </div>
      </InfoCard>

      {/* Description */}
      <InfoCard
        icon={<TextAlignStart size={14} />}
        iconColor="text-rose-500"
        accent="bg-rose-50"
        title={t("projects:description")}
        className="lg:col-span-3"
      >
        <p className="text-sm text-gray-700 leading-relaxed whitespace-pre-wrap">
          {project.description || t("projects:no_description_provided")}
        </p>
      </InfoCard>
    </div>
  );
};
